import { motion } from 'framer-motion';
import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { Toast as ToastType } from '@/lib/toast';
import { useToastStore } from '@/lib/toast';

type ToastProps = {
  toast: ToastType;
};

const iconMap = {
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
} as const;

const colorMap = {
  success: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    icon: 'text-green-600',
    text: 'text-green-800',
    button: 'text-green-500 hover:text-green-700 hover:bg-green-100',
  },
  error: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    icon: 'text-red-600',
    text: 'text-red-800',
    button: 'text-red-500 hover:text-red-700 hover:bg-red-100',
  },
  warning: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    icon: 'text-yellow-600',
    text: 'text-yellow-800',
    button: 'text-yellow-500 hover:text-yellow-700 hover:bg-yellow-100',
  },
  info: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    icon: 'text-blue-600',
    text: 'text-blue-800',
    button: 'text-blue-500 hover:text-blue-700 hover:bg-blue-100',
  },
} as const;

export function Toast({ toast }: ToastProps) {
  const { t } = useTranslation();
  const removeToast = useToastStore((state) => state.removeToast);

  const Icon = iconMap[toast.type];
  const colors = colorMap[toast.type];

  const handleDismiss = () => {
    removeToast(toast.id);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 50, scale: 0.9 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 50, scale: 0.9 }}
      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
      role="alert"
      aria-live="polite"
      className={`pointer-events-auto flex w-80 max-w-full items-start gap-3 rounded-lg border p-4 shadow-lg ${colors.bg} ${colors.border}`}
    >
      <Icon
        className={`h-5 w-5 flex-shrink-0 ${colors.icon}`}
        aria-hidden="true"
      />
      <p className={`flex-1 text-sm font-medium ${colors.text}`}>
        {t(toast.messageKey, toast.messageParams)}
      </p>
      {toast.dismissible && (
        <button
          type="button"
          onClick={handleDismiss}
          className={`flex-shrink-0 rounded-md p-1 transition-colors ${colors.button}`}
          aria-label={t('toast.dismiss')}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </motion.div>
  );
}
